import { BaseCrud } from '@/shared/models/cruds.models'
import { Component, Ref } from 'vue-property-decorator'
import ConfigurationSectionHeader from '@/modules/configurationModule/ConfigurationSectionHeader.vue'
import VtGrid from '@/components/VtGrid/VtGrid.vue'
import SampleTypesModal from '@/modules/configurationModule/detail/samples/samplesTypes/SampleTypesModal.vue'
import { IContentSampleTypesGrid, ICreatePatchContentSampleTypesGrid, initialSampleTypesGridState } from './SampleTypes.models'
import { sampleTypesConfigurationServices } from '@/api/resources/configuration/samples/sampleTypes/SampleTypesConfigurationServices'
import { GetContextMenuItemsParams, MenuItemDef, RowNode } from '@ag-grid-enterprise/all-modules'
import BaseCrudServerSideComponent from '@/shared/resources/base-crud-server-side.component'
import { EntityIdentifiersRequest } from '@/shared/models/http/http.model'
import { StrictNullable } from '@/shared/types/app.types'

@Component({
    components: {
        ConfigurationSectionHeader,
        VtGrid,
        SampleTypesModal
    }
})
export default class SampleTypesGrid extends BaseCrudServerSideComponent<IContentSampleTypesGrid> implements BaseCrud {
    @Ref() readonly sampleTypesModalWrapper!: any

    constructor() {
        super()
        this.state = initialSampleTypesGridState()
    }

    showTopographyModal(title:string | any, selectedTopography:IContentSampleTypesGrid) {
        this.state.actionTitle = title
        this.sampleTypesModalWrapper.show([selectedTopography])
    }

    getContextMenuItems(params:GetContextMenuItemsParams): MenuItemDef[] {
        return [
            {
                name: this.$t('configuration.actions.update').toString(),
                cssClasses: this.state.selectedRows.length > 1 ? ['not-show'] : [''],
                action: () => {
                    const nodeSelected:StrictNullable<RowNode> = params?.node
                    const selectedRow:IContentSampleTypesGrid = nodeSelected?.data
                    return this.showModal(this.$t('configuration.actions.update').toString(), selectedRow, this.sampleTypesModalWrapper)
                }
            },
            {
                name: this.$t('configuration.actions.delete').toString(),
                cssClasses: this.state.selectedRows.length < 1 ? ['not-show'] : [''],
                action: () => this.deleteOperation(this.state.selectedRows)
            },
            {
                name: params?.node?.data.active ? this.$t('configuration.actions.deactivate').toString() : this.$t('configuration.actions.activate').toString(),
                cssClasses: this.state.selectedRows.length ? ['not-show'] : [''],
                action: () => this.activateDeactivateSelection(!params?.node?.data?.active, [params?.node?.data])
            },
            {
                name: this.$t('configuration.actions.activate').toString(),
                cssClasses: this.canActivateBatch ? [''] : ['not-show'],
                action: () => this.activateDeactivateSelection(true, this.state.selectedRows)
            },
            {
                name: this.$t('configuration.actions.deactivate').toString(),
                cssClasses: this.canDeactivateBatch ? [''] : ['not-show'],
                action: () => this.activateDeactivateSelection(false, this.state.selectedRows)
            }
        ]
    }

    async createOperation(data: IContentSampleTypesGrid) {
        this.$_updateLoading(true)
        const sampleTypesToCreate: ICreatePatchContentSampleTypesGrid = {
            active: data?.active,
            code: data?.code,
            description: data?.description,
            workflow: data?.workflow?.code
        }
        const [err, response] = await this.$_toCall(sampleTypesConfigurationServices.createSampleTypes(sampleTypesToCreate))
        this.$_updateLoading(false)
        if (err) {
            this.$_showErrorModalFromErrorResponse(err)
            return
        } else if (response?.length) {
            this.$_handleErrors(response)
            return
        }
        this.sampleTypesModalWrapper.hide()
        this.$_showToastSuccess(this.$t('configuration.toastMessages.created')?.toString())
        this.reloadData()
    }

    async patchOperation(data: IContentSampleTypesGrid[]) {
        this.$_updateLoading(true)
        const sampleTypesToPatch: ICreatePatchContentSampleTypesGrid[] = data?.map((sampleType) => {
            return {
                id: sampleType?.id,
                active: sampleType?.active,
                code: sampleType?.code,
                description: sampleType?.description,
                workflow: sampleType?.workflow?.code
            }
        })
        const [err, response] = await this.$_toCall(sampleTypesConfigurationServices.patchSampleTypesBatch(sampleTypesToPatch))
        this.$_updateLoading(false)
        if (err) {
            this.$_showErrorModalFromErrorResponse(err)
            return
        } else if (response?.length) {
            this.$_handleErrors(response)
            return
        }
        this.sampleTypesModalWrapper.hide()
        this.$_showToastSuccess(this.$tc('configuration.toastMessages.updated', 1))
        this.reloadData()
    }

    async activateDeactivateSelection(option: boolean, selectionBatch: IContentSampleTypesGrid[]) {
        const sampleTypesToPatch:ICreatePatchContentSampleTypesGrid[] = selectionBatch?.filter((sampleType: IContentSampleTypesGrid) => sampleType.active !== option)?.map((sampleType:IContentSampleTypesGrid) => {
            return {
                ...sampleType,
                active: option,
                workflow: sampleType?.workflow?.code
            }
        })
        if (sampleTypesToPatch.length) {
            this.$_updateLoading(true)
            const [err] = await this.$_toCall(sampleTypesConfigurationServices.patchSampleTypesBatch(sampleTypesToPatch))
            this.$_updateLoading(false)
            if (err) {
                this.$_showErrorModalFromErrorResponse(err)
                return
            }
            if (selectionBatch.length > 1) {
                this.$_showToastSuccess(this.$tc('configuration.toastMessages.updated', 2, { elements: selectionBatch.length }))
            } else {
                this.$_showToastSuccess(this.$tc('configuration.toastMessages.updated', 1))
            }
            this.reloadData()
        }
    }

    async deleteBatchOperation(topographies: IContentSampleTypesGrid[]) {
        const sampleTypesToDelete: EntityIdentifiersRequest = {
            entityIdentifiers: topographies.map((sampleType: IContentSampleTypesGrid) => Number(sampleType?.id))
        }
        this.$_updateLoading(true)
        const [err, response] = await this.$_toCall(sampleTypesConfigurationServices.deleteSampleTypesBatch(sampleTypesToDelete))
        this.$_updateLoading(false)
        if (err) {
            this.$_showErrorModalFromErrorResponse(err)
            return
        } else if (response.length) {
            this.$_handleErrors(response)
            return
        }
        if (topographies.length > 1) {
            this.$_showToastSuccess(
                this.$tc('configuration.toastMessages.deleted', 2, { elements: topographies?.length })
            )
        } else {
            this.$_showToastSuccess(this.$tc('configuration.toastMessages.deleted', 1))
        }
        this.reloadData()
    }

    async deleteOperation(topographies: IContentSampleTypesGrid[]) {
        if (topographies.length) {
            const selectedRowsSize:number = this.state?.selectedRows?.length
            const labelConfirmation:string = this.$t('configuration.grid.deleteConfirmation')?.toString()
            const labelGrid: string = this.$tc('configuration.grid.element', selectedRowsSize)
            const labelDeleteQuestion:string = this.$t('configuration.grid.deleteQuestion')?.toString()
            const labelDeleteAction:string = this.$t('configuration.actions.delete')?.toString()
            const promise = await this.$_showConfirmationModal(
                `${labelConfirmation} ${selectedRowsSize} ${labelGrid}, <br/> ${labelDeleteQuestion}`,
                labelDeleteAction
            )
            if (promise) {
                this.deleteBatchOperation(topographies)
            }
        }
    }
}
